import { StorelocatorStorelocator } from './data-contracts';
import { HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class StoreLocator<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * @description Gets store location information by specified city street combination.
	 *
	 * @tags Store Locator
	 * @name StoreLocatorFindStoresByCityStreet
	 * @summary Get by specified city street combination
	 * @request GET:/store/{storeId}/storelocator/city_token/{cityToken}/street_token/{streetToken}
	 * @secure
	 * @response `200` `StorelocatorStorelocator` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	storeLocatorFindStoresByCityStreet = (
		storeId: string,
		cityToken: string,
		streetToken: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
			/** The maximum number of stores to return. */
			maxItems?: string;
			/** The radius unit of measure. */
			radiusUOM?: string;
			/** The physical store attribute name that describes whether the store is a beauty center. */
			BeautyCenter?: string;
			/** The physical store attribute name to describe the type of the store. */
			Type?: string;
			/** The radius. */
			radius?: string;
			/** If it is 'true', a site level physical search is performed.  Otherwise, the physical store search is performed at the web store level. By default, it is 'true'. */
			siteLevelStoreSearch?: 'true' | 'false';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['storeLocatorFindStoresByCityStreet'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/storelocator/city_token/${cityToken}/street_token/${streetToken}`,
				params: _params,
				query: query,
				methodName: 'storeLocatorFindStoresByCityStreet',
			};
			logger.trace(traceData);
		}
		return this.http.request<StorelocatorStorelocator, void>({
			path: `/store/${storeId}/storelocator/city_token/${cityToken}/street_token/${streetToken}`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Gets store location information by specified city street combination.
	 *
	 * @tags Store Locator
	 * @name StoreLocatorFindStoresByCity
	 * @summary Get by specified city street combination
	 * @request GET:/store/{storeId}/storelocator/city_token/{cityToken}
	 * @secure
	 * @response `200` `StorelocatorStorelocator` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	storeLocatorFindStoresByCity = (
		storeId: string,
		cityToken: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
			/** The maximum number of stores to return. */
			maxItems?: string;
			/** The radius unit of measure. */
			radiusUOM?: string;
			/** The physical store attribute name that describes whether the store is a beauty center. */
			BeautyCenter?: string;
			/** The physical store attribute name to describe the type of the store. */
			Type?: string;
			/** The radius. */
			radius?: string;
			/** If it is 'true', a site level physical search is performed.  Otherwise, the physical store search is performed at the web store level. By default, it is 'true'. */
			siteLevelStoreSearch?: 'true' | 'false';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['storeLocatorFindStoresByCity'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/storelocator/city_token/${cityToken}`,
				params: _params,
				query: query,
				methodName: 'storeLocatorFindStoresByCity',
			};
			logger.trace(traceData);
		}
		return this.http.request<StorelocatorStorelocator, void>({
			path: `/store/${storeId}/storelocator/city_token/${cityToken}`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Gets store location information by specified city street combination.
	 *
	 * @tags Store Locator
	 * @name StoreLocatorFindStoresByNearest
	 * @summary Get by specified city street combination
	 * @request GET:/store/{storeId}/storelocator/AjaxGetStoreLocatorByLocation
	 * @secure
	 * @response `200` `StorelocatorStorelocator` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	storeLocatorFindStoresByNearest = (
		storeId: string,
		latitude: string,
		longitude: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
			/** The maximum number of stores to return. */
			maxItems?: string;
			/** The radius unit of measure. */
			radiusUOM?: string;
			/** The physical store attribute name that describes whether the store is a beauty center. */
			BeautyCenter?: string;
			/** The physical store attribute name to describe the type of the store. */
			Type?: string;
			/** The radius. */
			radius?: string;
			/** If it is 'true', a site level physical search is performed.  Otherwise, the physical store search is performed at the web store level. By default, it is 'true'. */
			siteLevelStoreSearch?: 'true' | 'false';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['storeLocatorFindStoresByNearest'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/storelocator/AjaxGetStoreLocatorByLocation`,
				params: _params,
				query: query,
				methodName: 'storeLocatorFindStoresByNearest',
			};
			logger.trace(traceData);
		}
		return this.http.request<StorelocatorStorelocator, void>({
			path: `/store/${storeId}/storelocator/AjaxGetStoreLocatorByLocation`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
